/**
 * PATH: packages/ui/src/kanban/components/board-view/CardFaceCustomizePanel.tsx
 * FILE_ID: UI-KANBAN-CARD-FACE-CUSTOMIZE-001
 * PURPOSE: Board-settings card layout editor — preview plus show/hide and
 *          reorder for every card-face block.
 * WHY: Operator 2026-08-31 — settings should show a card image and let them
 *      pick what goes on the tile and in what order.
 * RELATED: ./cardFacePrefs.ts; ./CardFacePreview.tsx; ./BoardSettingsDrawer.tsx.
 */
import { Check, ChevronDown, ChevronUp } from 'lucide-react'

import { CardFacePreview } from './CardFacePreview'
import {
  CARD_FACE_BLOCK_IDS,
  REQUIRED_CARD_FACE_BLOCKS,
  isCardFaceBlockVisible,
  moveCardFaceBlock,
  toggleCardFaceBlock,
  type CardFaceBlockId,
  type KanbanCardFacePrefs,
} from './cardFacePrefs'

export const CARD_FACE_BLOCK_CATALOG: readonly {
  id: CardFaceBlockId
  label: string
  hint: string
}[] = [
  { id: 'labels', label: 'Colour labels', hint: 'Slim bars on compact cards' },
  { id: 'title', label: 'Title', hint: 'Always shown — the card name' },
  { id: 'actionLine', label: 'Next action', hint: 'Expanded cards only' },
  { id: 'stateStrip', label: 'Blocked strip', hint: 'Expanded cards only' },
  { id: 'emailMeta', label: 'Email from line', hint: 'Expanded email cards' },
  { id: 'meta', label: 'Checklist, chips, people', hint: 'The triage row' },
  { id: 'quoteFacts', label: 'Quote value and age', hint: 'Quote boards only' },
  { id: 'actionBar', label: 'Action icons', hint: 'Flag, date, assign, send…' },
  { id: 'capture', label: 'Note and Thread', hint: 'Type-a-note row + Thread' },
]

type CatalogIds = (typeof CARD_FACE_BLOCK_CATALOG)[number]['id']
const _exhaustive: readonly CatalogIds[] = CARD_FACE_BLOCK_IDS
void _exhaustive

export interface CardFaceCustomizePanelProps {
  prefs: KanbanCardFacePrefs
  setPrefs: (next: KanbanCardFacePrefs) => void
}

export function CardFaceCustomizePanel({
  prefs,
  setPrefs,
}: CardFaceCustomizePanelProps) {
  return (
    <div data-testid="card-face-customize">
      <CardFacePreview prefs={prefs} />
      <p className="mt-2 mb-1 text-[11px] text-neutral-400 dark:text-zinc-500">
        Preview is the compact card. Tick what shows. Arrows change the order.
      </p>
      <ul className="space-y-0.5">
        {prefs.order.map((id, index) => {
          const row = CARD_FACE_BLOCK_CATALOG.find((item) => item.id === id)
          if (!row) return null
          const required = REQUIRED_CARD_FACE_BLOCKS.includes(id)
          const shown = isCardFaceBlockVisible(prefs, id)
          return (
            <li
              key={id}
              data-testid={`settings-face-block-${id}`}
              className="flex items-center gap-1 rounded px-1 py-0.5 hover:bg-neutral-50 dark:hover:bg-zinc-900"
            >
              <button
                type="button"
                role="menuitemcheckbox"
                aria-checked={shown}
                aria-disabled={required || undefined}
                disabled={required}
                onClick={() => setPrefs(toggleCardFaceBlock(prefs, id))}
                className="flex min-w-0 flex-1 items-center gap-2 py-1 text-left text-xs text-neutral-700 disabled:cursor-not-allowed dark:text-zinc-200"
              >
                <span
                  className={[
                    'grid h-4 w-4 place-items-center rounded border',
                    shown
                      ? 'border-teal-600 bg-teal-600 text-white'
                      : 'border-neutral-300 dark:border-zinc-700',
                    required ? 'opacity-70' : '',
                  ].join(' ')}
                >
                  {shown && <Check className="h-3 w-3" />}
                </span>
                <span className="min-w-0">
                  <span className="block truncate">{row.label}</span>
                  <span className="block truncate text-[10px] text-neutral-400">
                    {row.hint}
                  </span>
                </span>
              </button>
              <div className="flex shrink-0 flex-col">
                <button
                  type="button"
                  aria-label={`Move ${row.label} up`}
                  disabled={index === 0}
                  onClick={() => setPrefs(moveCardFaceBlock(prefs, id, -1))}
                  className="rounded p-0.5 text-neutral-400 hover:bg-neutral-100 disabled:opacity-30 dark:hover:bg-zinc-800"
                >
                  <ChevronUp className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  aria-label={`Move ${row.label} down`}
                  disabled={index === prefs.order.length - 1}
                  onClick={() => setPrefs(moveCardFaceBlock(prefs, id, 1))}
                  className="rounded p-0.5 text-neutral-400 hover:bg-neutral-100 disabled:opacity-30 dark:hover:bg-zinc-800"
                >
                  <ChevronDown className="h-3 w-3" />
                </button>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
